import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

const getProcessedIconName = (iconName) => iconName.replace("icon-", "icon_");
const searchImageInSpriteSheet = (iconName) => {
	const icon = getProcessedIconName(iconName);
	return global.OC_spriteData && !!global.OC_spriteData[icon];
};

// This function calculates proportional width and height to maintain the aspect ratio
// based on the original dimensions and the provided custom dimension(s).
const calculateAspectDimensions = (originalWidth, originalHeight, customWidth, customHeight) => {
	const targetWidth = customWidth ? parseFloat(customWidth) : null;
	const targetHeight = customHeight ? parseFloat(customHeight) : null;

	if (!targetWidth && !targetHeight) { 
		return { width: `${originalWidth}px`, height: `${originalHeight}px` }; 
	}

	const aspectRatio = originalWidth / originalHeight;
	
	if (targetWidth && !targetHeight) {
		const aspectHeight = targetWidth / aspectRatio;
		return { width: `${targetWidth}px`, height: `${aspectHeight}px` };
	} else if (!targetWidth && targetHeight) {
		const aspectWidth = targetHeight * aspectRatio;
		return { width: `${aspectWidth}px`, height: `${targetHeight}px` };
	}

	// If both dimensions are provided, scale to fit w/in bounds while maintaining aspect ratio
	else {
		const widthRatio = targetWidth / originalWidth;
		const heightRatio = targetHeight / originalHeight;
		
		// Use the smaller ratio to ensure the sprite fits within both dimensions
		const scale = Math.min(widthRatio, heightRatio);
		
		const scaledWidth = originalWidth * scale;
		const scaledHeight = originalHeight * scale;
		
		return { width: `${scaledWidth}px`, height: `${scaledHeight}px` };
	}
};

const SpriteImageExtractor = ({ iconName, customWidth, customHeight, customStyles, imageFound, spriteImage, spriteData }) => {
	if (imageFound) {
		const [loaded, setLoaded] = useState(false);
		const canvasRef = useRef(null);
		const spriteImageRef = useRef(new Image());

		useEffect(() => {
			const loadImage = () => {
				spriteImageRef.current.onload = () => {
					setLoaded(true);
				};
				spriteImageRef.current.src = spriteImage.src;
			};

			if (spriteData && spriteImage && !loaded) loadImage();
		}, [spriteData, spriteImage]);

		useEffect(() => {
			if (!loaded) return;

			const frame = spriteData[iconName];
			const { x, y, width, height } = frame;

			const ctx = canvasRef.current.getContext("2d");
			ctx.clearRect(0, 0, width, height);
			ctx.drawImage(spriteImageRef.current, x, y, width, height, 0, 0, width, height);
		}, [loaded, iconName]);

		// Calculate aspect dimensions
		const originalWidth = spriteData[iconName]?.width || 0;
		const originalHeight = spriteData[iconName]?.height || 0;

		const { width, height } = calculateAspectDimensions(originalWidth, originalHeight, customWidth, customHeight);

		return (
			<canvas
				ref={canvasRef}
				width={loaded ? originalWidth : 0}
				height={loaded ? originalHeight : 0}
				style={{
					display: loaded ? "block" : "none",
					width,
					height,
					objectFit: "contain",
					...customStyles
				}}
			/>
		);
	}
	return <></>;
};

const mapStateToProps = (_, ownProps) => {
	if (ownProps?.iconName) {
		const processedIconName = ownProps.iconName !== "" ? getProcessedIconName(ownProps.iconName) : null;
		const imageFound = searchImageInSpriteSheet(processedIconName);

		// Check if iconName is not found, return a default value
		if (!imageFound) {
			return {
				iconName: null,
				imageFound: false,
				spriteImage: global.OC_spriteImage,
				spriteData: global.OC_spriteData
			};
		}

		return {
			iconName: processedIconName,
			imageFound: true,
			spriteImage: global.OC_spriteImage,
			spriteData: global.OC_spriteData
		};
	} else {
		return {
			iconName: null,
			imageFound: false,
			spriteImage: global.OC_spriteImage,
			spriteData: global.OC_spriteData
		};
	}
};

SpriteImageExtractor.propTypes = {
	iconName: PropTypes.string.isRequired,
	customWidth: PropTypes.string,
	customHeight: PropTypes.string,
	customStyles: PropTypes.object,
	imageFound: PropTypes.bool
};

SpriteImageExtractor.defaultProps = {
	customWidth: null,
	customHeight: null,
	customStyles: {},
	iconName: "",
	imageFound: true
};

const SpriteIcon = React.memo(SpriteImageExtractor);

export default connect(mapStateToProps)(SpriteIcon);
